import { createSlice } from "@reduxjs/toolkit";
import { getAuth, createUserWithEmailAndPassword, deleteUser } from "firebase/auth";

import {
  doc,
  collection,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  addDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../services/firebase";

export enum LocalStorageKeys {
  userToken = "userToken",
}
const initialState = {
  users: [],
  events: [],
  fetchingUsers: false,
};

export const userSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setFetchingUsers: (state, action) => {
      state.fetchingUsers = action.payload;
    },
    setEvents: (state, action) => {
      state.events = action.payload;
    },
  },
});
// Helper function to recover orphaned users (exist in Firebase Auth but not Firestore)
// This attempts to sign in with a temporary password to get the user's UID, then creates Firestore doc
// Note: This won't work if we don't know the password. Better solution: use Firebase Admin SDK
export const recoverOrphanedUser = async (
  email: string,
  firstname: string,
  lastname: string,
  company: string,
  profiles: string[]
) => {
  // This is a placeholder - actual recovery requires Admin SDK or knowing the password
  console.warn("Cannot recover orphaned user from client side. Use Firebase Console to delete the user from Authentication, then recreate.");
  return false;
};

export const createUser =
  (
    firstname: string,
    lastname: string,
    email: string,
    company: string,
    profiles: string[]
  ) =>
  async (dispatch: Function) => {
    dispatch(setFetchingUsers(true));
    try {
      const auth = getAuth();
      const newAuthUser = await createUserWithEmailAndPassword(
        auth,
        email,
        "Aa123456+"
      );
      //create new document in firestore with all properties
      const userDocRef = doc(db, "users", newAuthUser.user.uid);
      const newDBUser = {
        firstname,
        lastname,
        email,
        company,
        profiles,
        firstLogin: true,
        eulaAccepted: false,
        admin: false,
        analytics: {
          logins: [],
        },
      };
      await setDoc(userDocRef, newDBUser);
      await new Promise((r) => setTimeout(r, 1000));
      dispatch(setFetchingUsers(false));
      dispatch(getAllUsers()); // Refresh the user list
    } catch (error: any) {
      console.error("Error creating user:", error);
      let errorMessage = "An error occurred while creating the user.";
      
      if (error.code === "auth/email-already-in-use") {
        errorMessage = `The email "${email}" already exists in Firebase Authentication but may not appear in your user list. This typically happens when:\n\n1. The user exists in Firebase Auth but the Firestore document was never created\n2. The Firestore document was deleted but the Auth user remains\n3. A previous creation attempt partially succeeded\n\nTo fix this:\n- Go to Firebase Console > Authentication and delete the user with email "${email}"\n- Then try creating the user again\n\nAlternatively, use Firebase Admin SDK to list and clean up orphaned users.`;
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "The email address is invalid.";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "The password is too weak.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Log detailed error for debugging
      console.error("User creation error details:", {
        code: error.code,
        message: error.message,
        email: email
      });
      
      alert(errorMessage);
      dispatch(setUsers(false));
      dispatch(setFetchingUsers(false));
    }
  };

export const updateUser =
  (
    id: string,
    firstname: string,
    lastname: string,
    email: string,
    company: string,
    profiles: string[]
  ) =>
  async (dispatch: Function) => {
    dispatch(setFetchingUsers(true));
    try {
      const userDocRef = doc(db, "users", id);
      const dbUser = await getDoc(userDocRef);
      const userData = dbUser.data();
      const newDBUser = {
        ...userData,
        firstname,
        lastname,
        email,
        company,
        profiles,
      };
      setDoc(userDocRef, newDBUser);
      await new Promise((r) => setTimeout(r, 1000));
      dispatch(setFetchingUsers(false));
    } catch (error) {
      console.error(error);
      dispatch(setUsers(false));
    }
  };

export const getAllUsers = () => async (dispatch: Function) => {
  try {
    const userRef = collection(db, "users");
    const users = await getDocs(userRef);
    const formattedUsers = users.docs.map((doc) => {
      return { id: doc.id, ...doc.data() };
    });
    dispatch(setUsers(formattedUsers));
  } catch (error) {
    console.error(error);
  }
};

export const deleteUserById = (user: any) => async (dispatch: Function) => {
  try {
    const auth = getAuth();
    // Delete from Firestore
    const userRef = doc(db, "users", user.id);
    await deleteDoc(userRef);
    
    // Note: Deleting from Firebase Authentication requires Admin SDK
    // The current implementation only deletes if it's the current user
    // For other users, you need to delete them via Firebase Console or Admin SDK
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.uid === user.id) {
      await deleteUser(currentUser);
    } else {
      console.warn(`User ${user.id} (${user.email}) deleted from Firestore but not from Firebase Authentication. Delete manually via Firebase Console or use Admin SDK.`);
    }
    
    await new Promise((r) => setTimeout(r, 1000));
    dispatch(getAllUsers());
  } catch (error: any) {
    console.error("Error deleting user:", error);
    alert(`Error deleting user: ${error.message || "Unknown error"}`);
  }
};

// Function to find orphaned users (exist in Firebase Auth but not in Firestore)
// Note: This requires checking each user individually, which is not efficient
// For production, use Firebase Admin SDK to list all auth users
export const findOrphanedAuthUsers = async (): Promise<string[]> => {
  try {
    // Get all Firestore users
    const userRef = collection(db, "users");
    const usersSnapshot = await getDocs(userRef);
    const firestoreUserIds = new Set(usersSnapshot.docs.map(doc => doc.id));
    
    // Note: We cannot list all Firebase Auth users from the client side
    // This would require Firebase Admin SDK on the backend
    // This function is a placeholder to show the concept
    
    console.warn("Cannot list Firebase Auth users from client side. Use Firebase Admin SDK or Firebase Console to check for orphaned users.");
    return [];
  } catch (error) {
    console.error("Error finding orphaned users:", error);
    return [];
  }
};
export const addEvent = (event: any) => async (dispatch: Function) => {
  try {
    const eventRef = collection(db, "events");
    await addDoc(eventRef, event);
    await new Promise((r) => setTimeout(r, 1000));
  } catch (error) {
    console.error(error);
  }
};

export const updateAnalyticsLogins =
  (id: string) => async (dispatch: Function) => {
    try {
      const userDocRef = doc(db, "users", id);
      const dbUser = await getDoc(userDocRef);
      const userData = dbUser.data();
      const newConn = {
        date: new Date().toISOString(),
        duration: 0,
      };
      const newLogins = [...userData.analytics.logins, newConn];
      updateDoc(userDocRef, {
        "analytics.logins": newLogins,
      });
      await new Promise((r) => setTimeout(r, 1000));
    } catch (error) {
      console.error(error);
    }
  };

export const getEventsSortedByDate = () => async (dispatch: Function) => {
  try {
    const q = query(collection(db, "events"), orderBy("date", "desc")); // "desc" for descending order, "asc" for ascending
    const querySnapshot = await getDocs(q);
    const formattedEvents = [];
    querySnapshot.forEach((doc) => {
      formattedEvents.push({ ...doc.data() });
    });
    dispatch(setEvents(formattedEvents));
  } catch (error) {
    console.error(error);
  }
};

export const migrateUsers = () => async (dispatch: Function) => {
  try {
    const userRef = collection(db, "events");
    const users = await getDocs(userRef);
    users.forEach(async (document) => {
      const user = document.data();
      const userDocRef = doc(db, "events", document.id);
      if (Object.hasOwn(user.profile, "code")) {
        updateDoc(userDocRef, {
          profile: user.profile.code,
        });
      }
    });
    await new Promise((r) => setTimeout(r, 1000));
    dispatch(getAllUsers());
  } catch (error) {
    console.error(error);
  }
};

export const updateConnectionTime =
  (id: string, connectionTime: number) => async (dispatch: Function) => {
    try {
      const userDocRef = doc(db, "users", id);
      const dbUser = await getDoc(userDocRef);
      const userData = dbUser.data();
      const lastLogin =
        userData.analytics.logins[userData.analytics.logins.length - 1];
      lastLogin.duration += connectionTime;
      updateDoc(userDocRef, {
        "analytics.logins": [...userData.analytics.logins],
      });
      await new Promise((r) => setTimeout(r, 1000));
      dispatch(getAllUsers());
    } catch (error) {
      console.error(error);
    }
  };
export const { setUsers, setEvents, setFetchingUsers } = userSlice.actions;

export default userSlice.reducer;
